/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import Typography from 'carbon-react/lib/components/typography';
import Textbox from 'carbon-react/lib/components/textbox';
import Password from 'carbon-react/lib/components/password';
import Button from 'carbon-react/lib/components/button';

import { FormattedMessage, IntlShape, useIntl } from '@futrli/localisation';
import {
  appAuthenticator,
  createPasswordValidator,
  Logger,
  CognitoUser,
  ONLY_DIGITS,
  isPending
} from '@futrli/shared-services';
import { UILoaderSpinner } from '@futrli/ui-components';

import { StoreState } from '../../core/types';
import { setPreferredMFAType, updateUserAttributes } from '../../auth/redux-utils/actions';
import { disableUsersMfa } from '../my-profile-routes';
import { VERIFICATION_CODE_LENGTH } from './multi-factor-auth';
import { getPhoneNumberMask } from '../utils';

interface Props {
  handleSwitchClick: (value: boolean) => void;
}

const getErrorMessageText = (
  passwordError: boolean,
  passwordTouched: boolean,
  passwordValid: boolean,
  intl: IntlShape
) => {
  return passwordError
    ? intl.formatMessage({ id: 'my_profile.invalid_password', defaultMessage: 'Invalid password' })
    : passwordTouched &&
        !passwordValid &&
        intl.formatMessage({
          id: 'validation.invalid_password_description',
          defaultMessage:
            'Sorry, your password must be at least 9 or more characters and contain a capital letter, number and a symbol.'
        });
};

const getVerificationCodeLabel = (cognitoUser: CognitoUser | null, phoneNumberMask: string, intl: IntlShape) => {
  if (cognitoUser) {
    return intl.formatMessage(
      {
        id: 'login.sms_sent_number_ending_in',
        defaultMessage: 'An SMS with a code has been sent to mobile number ending in {phoneNumberMask}.'
      },
      { phoneNumberMask }
    );
  }
  return intl.formatMessage({ id: 'common.verification_code', defaultMessage: 'Verification code' });
};

export const DisableMfa = ({ handleSwitchClick }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState(false);

  const dispatch = useDispatch();

  const intl = useIntl();

  const { user, isLoadingSetPreferredMfaType } = useSelector((state: StoreState) => ({
    user: state.authReducers.authenticatedUser,
    isLoadingSetPreferredMfaType: isPending(state.loadingRequests.SET_PREFERRED_MFA_TYPE)
  }));

  useEffect(() => {
    setEmail(get(user, 'attributes.email', ''));
  }, [user]);

  const setInitialValues = () => {
    setPassword('');
    setPasswordError(false);
    setPasswordTouched(false);
    setCognitoUser(null);
    setVerificationCode('');
    setVerificationError(false);
    handleSwitchClick(false);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    setPasswordTouched(true);
    setPasswordError(false);
  };

  const handleVerificationCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVerificationCode(event.target.value);
    setVerificationError(false);
  };

  const handlePasswordSubmit = async () => {
    try {
      setIsLoading(true);
      const authUser = await appAuthenticator.signIn(email, password);
      setCognitoUser(authUser as CognitoUser);
    } catch (e) {
      Logger.error(e as string);
      setPasswordError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationCodeSubmit = async () => {
    try {
      setIsLoading(true);
      await appAuthenticator.confirmSignIn(cognitoUser, verificationCode, 'SMS_MFA');
      await disableUsersMfa(user.username);
      await Promise.all([setPreferredMFAType('NOMFA')(dispatch), updateUserAttributes({ phone_number: '' })(dispatch)]);
      setInitialValues();
    } catch (e) {
      Logger.error(e as string);
      setVerificationError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelClick = () => {
    setInitialValues();
    handleSwitchClick(false);
  };

  const passwordValidator = createPasswordValidator();

  const passwordValid = passwordValidator(password).isValid;

  const isValidVerificationCode =
    ONLY_DIGITS.test(verificationCode) && verificationCode.length === VERIFICATION_CODE_LENGTH;

  const submitDisabled = isLoading || !cognitoUser || !isValidVerificationCode;

  const phoneNumber = get(user, 'attributes.phone_number', '');

  let phoneNumberMask = '';
  if (phoneNumber) {
    phoneNumberMask = getPhoneNumberMask(phoneNumber);
  }

  if (isLoading || isLoadingSetPreferredMfaType) {
    return <UILoaderSpinner />;
  }

  return (
    <div>
      <Typography mb="15px" fontWeight="700" fontSize="18px">
        <FormattedMessage id="my_profile.turn_off_mfa" defaultMessage="Turn off multi-factor authentication" />
      </Typography>
      <Textbox
        label={intl.formatMessage({ id: 'common.email_address', defaultMessage: 'Email address' })}
        value={email}
        onChange={() => undefined}
        disabled={true}
        mb="20px"
        size="large"
      />
      <Password
        label={intl.formatMessage({
          id: 'common.password',
          defaultMessage: 'Password'
        })}
        placeholder={intl.formatMessage({
          id: 'my_profile.enter_password',
          defaultMessage: 'Enter password'
        })}
        value={password}
        onChange={handlePasswordChange}
        mb="30px"
        error={
          (!passwordValid && passwordTouched) || passwordError
            ? getErrorMessageText(passwordError, passwordTouched, passwordValid, intl)
            : ''
        }
        disabled={isLoading}
        size="large"
      />
      <div style={{ display: 'flex', marginBottom: 20 }}>
        <Button
          disabled={!passwordValid || !!cognitoUser}
          onClick={handlePasswordSubmit}
          ml="auto"
          buttonType="primary"
          size="medium"
        >
          <FormattedMessage id="my_profile.send_code" defaultMessage="Send code" />
        </Button>
      </div>
      <Textbox
        label={getVerificationCodeLabel(cognitoUser, phoneNumberMask, intl)}
        placeholder={intl.formatMessage({
          id: 'my_profile.enter_verification_code_here',
          defaultMessage: 'Enter verification code here'
        })}
        value={verificationCode}
        onChange={handleVerificationCodeChange}
        disabled={!cognitoUser}
        error={
          verificationError
            ? intl.formatMessage({
                id: 'my_profile.invalid_verification_code',
                defaultMessage: 'Invalid verification code provided, please try again.'
              })
            : ''
        }
        size="large"
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ display: 'flex', marginTop: 30 }}>
          <Button buttonType="secondary" size="medium" mr="12px" onClick={handleCancelClick}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button buttonType="primary" size="medium" disabled={submitDisabled} onClick={handleVerificationCodeSubmit}>
            <FormattedMessage id="my_profile.disable_mfa" defaultMessage="Disable MFA" />
          </Button>
        </div>
      </div>
    </div>
  );
};
